/**
 * Client store for the Waste Logs screen.
 *
 * Holds the log list and the list's UI state (search text, page, page size,
 * filters, which overlay is open). Rows arrive from `GET waste/logs/` through
 * the SSR fetcher and are hydrated here by WasteLogsInitializer — the slice
 * itself never fetches, and the list renders the rows exactly as given
 * (search/filter/paging are answered server-side via WasteQuerySync).
 *
 * The Add/Edit form's draft is deliberately NOT here: every part of that form
 * (the lines, the "N Items added" counter, the footer, the preview) lives in
 * one component subtree, so it stays local `useState` per conventions §9.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { WasteLog, WasteLogPage } from "@/components/waste-logs/types";

/**
 * Which overlay is on screen. Only one can be open at a time, so this is a
 * single discriminator rather than a boolean per drawer.
 */
export type WasteOverlay = "details" | "edit";

/**
 * Facets the Filter popover applies (Figma 2967:53520). Each value is a
 * vocabulary **uuid** — what `waste/logs/` filters by — and an empty string
 * means "no filter", which keeps each facet a plain single-select.
 */
export interface WasteFilters {
  station: string;
  category: string;
  reason: string;
}

export const EMPTY_WASTE_FILTERS: WasteFilters = {
  station: "",
  category: "",
  reason: "",
};

export interface WasteLogsState {
  logs: WasteLog[];
  /** Total rows across all pages — drives "1-10 of 180". */
  total: number;
  page: number;
  pageSize: number;
  search: string;
  filters: WasteFilters;
  /** Open overlay, or null when the list is uncovered. */
  overlay: WasteOverlay | null;
  /** Log the open overlay acts on. */
  activeLogId: string | null;
}

const initialState: WasteLogsState = {
  logs: [],
  total: 0,
  page: 1,
  pageSize: 10,
  search: "",
  filters: EMPTY_WASTE_FILTERS,
  overlay: null,
  activeLogId: null,
};

const wasteLogsSlice = createSlice({
  name: "wasteLogs",
  initialState,
  reducers: {
    /** Replace the list with a server-rendered page (called by the initializer). */
    setWasteLogs(state, action: PayloadAction<WasteLogPage>) {
      state.logs = action.payload.logs;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setWastePage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setWastePageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      // A larger page size can put the current page out of range
      state.page = 1;
    },
    setWasteSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      // Any new query restarts paging from the first page
      state.page = 1;
    },
    setWasteFilters(state, action: PayloadAction<WasteFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearWasteFilters(state) {
      state.filters = EMPTY_WASTE_FILTERS;
      state.page = 1;
    },
    /** Open an overlay against a row. */
    openWasteOverlay(
      state,
      action: PayloadAction<{ overlay: WasteOverlay; logId: string }>,
    ) {
      state.overlay = action.payload.overlay;
      state.activeLogId = action.payload.logId;
    },
    closeWasteOverlay(state) {
      state.overlay = null;
      state.activeLogId = null;
    },
  },
});

export const {
  setWasteLogs,
  setWastePage,
  setWastePageSize,
  setWasteSearch,
  setWasteFilters,
  clearWasteFilters,
  openWasteOverlay,
  closeWasteOverlay,
} = wasteLogsSlice.actions;

export default wasteLogsSlice.reducer;
